import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- Hybrid auth strategy ----
// Same-origin (preview, or properly same-origin production): use httpOnly cookie.
// Cross-origin (production frontend ≠ backend domain, e.g., myhisaabpro.com calling
//   qist-manager-2.emergent.host): the platform ingress injects
//   `Access-Control-Allow-Origin: *`, which browsers REJECT together with
//   `Access-Control-Allow-Credentials: true`. So we must send the request WITHOUT
//   credentials and authenticate via `Authorization: Bearer` from sessionStorage.
function isCrossOrigin() {
  try {
    return new URL(BACKEND_URL).origin !== window.location.origin;
  } catch {
    return false;
  }
}

const USE_CREDENTIALS = !isCrossOrigin();

const TOKEN_KEY = "hp_token";

// Native Android (Capacitor) keeps the user logged in across app restarts
// by mirroring the token to localStorage. On the web we stay with
// sessionStorage so closing the browser tab still logs out, matching the
// existing behaviour.
const _isNative = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
  catch { return false; }
};
const _store = () => (_isNative() ? window.localStorage : window.sessionStorage);

export const setSessionToken = (token) => {
  const s = _store();
  if (token) s.setItem(TOKEN_KEY, token);
  else s.removeItem(TOKEN_KEY);
};
export const getSessionToken = () => {
  // Prefer the active store; fall back to the other so a one-time migration
  // works (e.g. APK upgrade from Phase 1 → Phase 2).
  return _store().getItem(TOKEN_KEY)
    || window.sessionStorage.getItem(TOKEN_KEY)
    || window.localStorage.getItem(TOKEN_KEY);
};
export const clearSessionToken = () => {
  window.sessionStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(TOKEN_KEY);
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: USE_CREDENTIALS,
});

api.interceptors.request.use((config) => {
  const tok = getSessionToken();
  if (tok && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearSessionToken();
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register" && path !== "/") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err_or_detail) {
  // Accept either the raw axios error (preferred) or just the detail field (legacy callers).
  const detail = err_or_detail?.response?.data?.detail ?? err_or_detail;

  // Network / CORS failure — show something actionable instead of swallowing it.
  if (err_or_detail?.isAxiosError && !err_or_detail.response) {
    const msg = err_or_detail.message || "Network error";
    return `Connection failed: ${msg}. Check your internet or try again.`;
  }

  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.message) return detail.message;
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/** True when an API error is a 402 plan-limit reached. */
export function isLimitError(err) {
  const d = err?.response?.data?.detail;
  const code = typeof d === "object" ? d?.code : null;
  return err?.response?.status === 402 && (code === "customer_limit_reached" || code === "active_loan_limit_reached");
}

export const fmtPKR = (n) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
